import React from 'react';
import { formatarPercentual, TRACO } from '../../utils/series';
import {
  Box,
  Typography,
  Grid,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  AlertTitle
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import { GRUPOS } from '../../config/planoContas';
import { listarEmFrase, destinoDosMeses } from '../../services/mesesPermitidos';
import { plural } from '../../services/loteDoMes';

const moeda = (valor) =>
  (Number(valor) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const somaGrupo = (contas, campo) =>
  (contas || []).reduce((s, c) => s + (Number(c[campo]) || 0), 0);

/**
 * Fecha o questionário mostrando o resultado antes de gerar o dashboard.
 * O empresário precisa reconhecer os números aqui — se o lucro sair
 * estranho, é aqui que ele percebe que esqueceu uma conta.
 *
 * É também onde mês em branco é NOMEADO. Com três meses ninguém deixava um
 * vazio; com doze é o caso comum — a pessoa conhece o segundo semestre e não o
 * primeiro. Mês em branco não é erro: ele fica de fora da série, e é dito por
 * nome para ninguém procurar depois um número que nunca foi informado.
 *
 * E é onde a SOBREPOSIÇÃO é declarada antes do clique. Antes desta onda, gerar
 * o dashboard sobre um mês já lançado o regravava em silêncio: dezembro tinha
 * R$ 120.000 guardados, a pessoa preenchia dezembro de novo e o número trocava
 * sem uma linha de aviso. Substituir continua permitido — é assim que se
 * corrige o próprio dado, e não consome cota —, mas agora é decisão informada.
 *
 * @param {Array} preenchidos — rótulos dos meses que realmente têm valor.
 * @param {Array} descartados — rótulos dos meses em branco, em qualquer posição.
 * @param {Object|null} plano — o que subiu do StepConfig: limite, nome e as
 *   competências que a empresa já tem. `null` para visitante anônimo, onde nada
 *   é gravado e não há o que anunciar.
 */
const StepRevisao = ({
  formData,
  meses,
  preenchidos = null,
  descartados = [],
  plano = null,
  // O catalogo depende das respostas do perfil; o padrao atende quem nao passou
  // por ele.
  catalogo = GRUPOS
}) => {
  const { grupos, balanco, config } = formData;

  const mesesComValor = preenchidos || meses;
  const destino = plano
    ? destinoDosMeses({
        meses: mesesComValor,
        limiteCompetencias: plano.limiteCompetencias,
        competenciasJaLancadas: plano.competenciasJaLancadas
      })
    : null;

  const camposComValor = new Set(mesesComValor.map((m) => m.campo));

  const porMes = meses.map((mes) => {
    const receita = somaGrupo(grupos.receitas, mes.campo);
    const variaveis = somaGrupo(grupos.custosVariaveis, mes.campo);
    const fixos = ['pessoal', 'socios', 'administrativas', 'financeiras', 'marketing'].reduce(
      (s, id) => s + somaGrupo(grupos[id], mes.campo),
      0
    );
    return {
      ...mes,
      // Mês sem nenhum valor digitado não tem número NENHUM para mostrar: a
      // coluna sai em travessão. Somar seus campos vazios daria R$ 0,00 e a
      // tabela afirmaria faturamento zero num mês que ninguém mediu — o defeito
      // que este produto trata como o mais grave, na tela em que o usuário
      // confere antes de gravar.
      informado: camposComValor.has(mes.campo),
      receita,
      variaveis,
      fixos,
      lucro: receita - variaveis - fixos,
      margem: receita > 0 ? ((receita - variaveis - fixos) / receita) * 100 : 0
    };
  });

  const receitaTotal = porMes.reduce((s, m) => s + m.receita, 0);
  const contasPreenchidas = catalogo.reduce(
    (s, g) =>
      s +
      (grupos[g.id] || []).filter((c) =>
        meses.some((m) => Number(c[m.campo]) > 0)
      ).length,
    0
  );

  return (
    <Box>
      <Box sx={{ mb: 3, p: 2, bgcolor: 'success.main', color: 'success.contrastText', borderRadius: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          Confira antes de gerar o dashboard
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.95 }}>
          {config.empresa || 'Sua empresa'} · {plural(contasPreenchidas, 'conta preenchida', 'contas preenchidas')}
        </Typography>
      </Box>

      {receitaTotal === 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          O faturamento está zerado. Volte ao primeiro grupo e informe as vendas —
          sem receita não há indicador de margem, ponto de equilíbrio nem lucro.
        </Alert>
      )}

      {descartados.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>
            {listarEmFrase(descartados.map((m) => m.longo))}{' '}
            {descartados.length === 1 ? 'ficou em branco' : 'ficaram em branco'}
          </AlertTitle>
          {descartados.length === 1 ? 'Esse mês vai' : 'Esses meses vão'} ficar de fora: você fecha
          com {mesesComValor.length}{' '}
          {mesesComValor.length === 1 ? 'mês' : 'meses'}. Deixar em branco não é o mesmo que
          faturamento zero, e este produto não afirma medição que ninguém fez — no histórico esses
          meses aparecem como travessão.
        </Alert>
      )}

      {/* O que vai acontecer com cada mês, ANTES do clique. Substituir mês
          existente é correção e continua liberado; o que não pode é acontecer
          calado. */}
      {destino && destino.existentes.length > 0 && (
        <Alert severity="warning" icon={<HistoryIcon />} sx={{ mb: 2 }}>
          <AlertTitle>
            {listarEmFrase(destino.existentes.map((m) => m.longo))}{' '}
            {destino.existentes.length === 1
              ? 'já está lançado e vai ser substituído'
              : 'já estão lançados e vão ser substituídos'}
          </AlertTitle>
          O que está na tabela abaixo passa a valer no lugar do que está guardado. A versão
          anterior continua no histórico do mês, e corrigir o próprio dado não consome o limite
          do plano. Se não era isso que você queria, volte e apague os valores{' '}
          {destino.existentes.length === 1 ? 'desse mês' : 'desses meses'}.
        </Alert>
      )}

      {destino && destino.bloqueados.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>
            {listarEmFrase(destino.bloqueados.map((m) => m.longo))}{' '}
            {destino.bloqueados.length === 1 ? 'não vai' : 'não vão'} entrar no histórico salvo
          </AlertTitle>
          Seu plano {plano.nomeDoPlano} acompanha {plano.limiteCompetencias} meses de histórico, e
          eles já estão ocupados. O diagnóstico na tela usa tudo que você informou — o que fica de
          fora é só o histórico guardado.
        </Alert>
      )}

      {balanco?.preenchido === false && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Balanço não preenchido: liquidez, endividamento, ROE e ROA vão aparecer como
          <strong> requer balanço</strong>.
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Resultado</strong></TableCell>
              {porMes.map((m) => (
                <TableCell key={m.campo} align="right"><strong>{m.curto}</strong></TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Faturamento</TableCell>
              {porMes.map((m) => (
                <TableCell key={m.campo} align="right">
                  {m.informado ? moeda(m.receita) : TRACO}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>(−) Custos variáveis</TableCell>
              {porMes.map((m) => (
                <TableCell key={m.campo} align="right">
                  {m.informado ? moeda(m.variaveis) : TRACO}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>(−) Custos fixos</TableCell>
              {porMes.map((m) => (
                <TableCell key={m.campo} align="right">
                  {m.informado ? moeda(m.fixos) : TRACO}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell><strong>= Lucro</strong></TableCell>
              {porMes.map((m) => (
                <TableCell
                  key={m.campo}
                  align="right"
                  sx={{
                    color: m.informado ? (m.lucro >= 0 ? 'success.main' : 'error.main') : 'inherit',
                    fontWeight: 'bold'
                  }}
                >
                  {m.informado ? moeda(m.lucro) : TRACO}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Margem</TableCell>
              {porMes.map((m) => (
                <TableCell key={m.campo} align="right">
                  {m.informado && m.receita > 0 ? formatarPercentual(m.margem, 1) : TRACO}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Card>

      <Typography variant="subtitle2" gutterBottom fontWeight="bold">
        Por grupo
      </Typography>
      <Grid container spacing={1.5}>
        {catalogo.map((g) => {
          const total = meses.reduce((s, m) => s + somaGrupo(grupos[g.id], m.campo), 0);
          const usadas = (grupos[g.id] || []).filter((c) =>
            meses.some((m) => Number(c[m.campo]) > 0)
          ).length;
          return (
            <Grid item xs={12} sm={6} md={4} key={g.id}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">
                    {g.titulo}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {moeda(total)}
                  </Typography>
                  <Chip
                    size="small"
                    icon={usadas > 0 ? <CheckCircleOutlineIcon /> : undefined}
                    label={usadas > 0 ? plural(usadas, 'conta', 'contas') : 'não informado'}
                    color={usadas > 0 ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ mt: 0.5 }}
                  />
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default StepRevisao;
